/**
 * exportIabSheet.js
 * 
 * Flattens a list of network requests (OpenRTB, GAM, APS, etc.) into a CSV format
 * suitable for an "IAB Audit" sheet.
 * 
 * Features:
 * - Dynamic Column Discovery: Scans all selected rows to find every unique field (flattened).
 * - UI Parity: Uses the same flattening and parsing logic as the Comparison View.
 * - Grouped Columns: Orders columns by logical sections (Imp, App, Site, Device, User, etc.).
 */

/** ---------- Shared Helpers (Ported from CompareRequestsModal.jsx) ---------- */

/** parse simple x-www-form-urlencoded / querystring-like body into a key/value map */
function parseFormBody(raw) {
    if (!raw || typeof raw !== "string") return null;
    if (!raw.includes("=")) return null;
    const out = {};
    const src = raw.trim();
    src.split("&").forEach((pair) => {
        if (!pair) return;
        const [kPart, ...vParts] = pair.split("=");
        const key = kPart || "";
        const value = vParts.join("=");
        if (!key) return;
        const normKey = key.replace(/\+/g, " ");
        const normVal = (value || "").replace(/\+/g, " ");
        try {
            const dk = decodeURIComponent(normKey);
            const dv = decodeURIComponent(normVal);
            out[dk] = dv;
        } catch {
            out[key] = value || "";
        }
    });
    return Object.keys(out).length ? out : null;
}

/**
 * Parse nested querystring-like values such as `cust_params`
 */
function parseNestedParams(raw) {
    if (!raw || typeof raw !== "string") return null;
    let src = raw.startsWith("?") ? raw.slice(1) : raw;
    let parsed = parseFormBody(src);
    if (parsed) return parsed;
    try {
        const decoded = decodeURIComponent(src.replace(/\+/g, " "));
        parsed = parseFormBody(decoded);
        if (parsed) return parsed;
    } catch { }
    return null;
}

/** flatten object into path->string (arrays included) */
function flatten(obj, prefix = "", out = {}) {
    if (obj == null) { out[prefix || "(empty)"] = ""; return out; }
    if (typeof obj !== "object") { out[prefix || "(value)"] = obj; return out; }
    if (Array.isArray(obj)) {
        if (obj.length === 0) out[prefix || "(array)"] = "[]";
        obj.forEach((v, i) => flatten(v, prefix ? `${prefix}[${i}]` : `[${i}]`, out));
        return out;
    }
    const keys = Object.keys(obj);
    if (keys.length === 0) out[prefix || "(object)"] = "{}";
    keys.forEach((k) => flatten(obj[k], prefix ? `${prefix}.${k}` : k, out));
    return out;
}

function safeJson(input) {
    if (typeof input === 'object' && input !== null) return input;
    try { return JSON.parse(input); } catch { return null; }
}

/** Use the same logic as Comparison View to decode cust_params before flattening */
function prepareBodyForFlatten(row) {
    // 1. Try to parse JSON body
    let body = safeJson(row.requestBody);

    // 2. If not JSON, try to parse as form body (IMA, etc.)
    if (!body) {
        body = parseFormBody(row.requestBody) || {};
    }

    // 3. Decode cust_params if present
    if (body && typeof body === 'object') {
        const out = { ...body };
        // Check for cust_params in top level (common in IMA/GAM)
        if (typeof out.cust_params === "string") {
            const parsed = parseNestedParams(out.cust_params);
            if (parsed) {
                out.cust_params_raw = out.cust_params; // keep original
                out.cust_params = parsed;              // expand nested
            }
        }
        return out;
    }
    return {};
}

// Helper: Escape CSV fields
const esc = (val) => {
    const s = String(val === null || val === undefined ? '' : val);
    if (s.includes(',') || s.includes('"') || s.includes('\n')) {
        return `"${s.replace(/"/g, '""')}"`;
    }
    return s.replace(/\s+/g, ' '); // collapse multiline whitespace
};


/** ---------- Main Export Logic ---------- */

export function generateIabSheet(rows) {
    // 1. Collect all flattened rows
    const flattenedRows = rows.map(r => {
        const flatBody = flatten(prepareBodyForFlatten(r));
        return {
            meta: {
                // Standard Meta Columns
                Timestamp: r.startedAt ? new Date(r.startedAt).toISOString() : '',
                Method: r.method || '',
                Host: r.url ? new URL(r.url).hostname : '',
                Type: r.type || 'unknown',
                RequestID: r.id || r.requestId || '',
                Status: r.status || '',
            },
            body: flatBody
        };
    });

    // 2. Discover ALL unique keys from bodies
    const bodyKeysSet = new Set();
    flattenedRows.forEach(item => {
        Object.keys(item.body).forEach(k => bodyKeysSet.add(k));
    });

    // 3. Sort Body Keys by Section Priority (UI Parity)
    const PRIORITIES = ["imp", "app", "site", "device", "user", "regs", "source", "ext", "cust_params"];
    const sortedBodyKeys = Array.from(bodyKeysSet).sort((a, b) => {
        // Extract top-level key (e.g. "imp[0].id" -> "imp")
        const topA = a.split('.')[0].split('[')[0].toLowerCase();
        const topB = b.split('.')[0].split('[')[0].toLowerCase();

        const idxA = PRIORITIES.indexOf(topA);
        const idxB = PRIORITIES.indexOf(topB);

        // If both are priority keys and different groups, sort by group priority
        if (idxA !== -1 && idxB !== -1 && topA !== topB) return idxA - idxB;
        if (idxA !== -1 && idxB === -1) return -1; // A is priority, B is not -> A first
        if (idxA === -1 && idxB !== -1) return 1;  // B is priority, A is not -> B first

        // Otherwise alphabetical
        return a.localeCompare(b);
    });

    // 4. Build Header
    const metaHeaders = ['Timestamp', 'Method', 'Host', 'Type', 'RequestID', 'Status'];
    const allHeaders = [...metaHeaders, ...sortedBodyKeys];

    // 5. Build CSV Rows
    const dataLines = flattenedRows.map(item => {
        const metaVals = metaHeaders.map(h => item.meta[h]);
        const bodyVals = sortedBodyKeys.map(k => item.body[k] ?? '');
        return [...metaVals, ...bodyVals].map(esc).join(',');
    });

    return [allHeaders.join(','), ...dataLines].join('\n');
}

export function downloadIabSheet(rows, filename = 'iab-audit-export.csv') {
    const csvContent = generateIabSheet(rows);
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }
}
